import { tt } from '@agos/shared'
import type { PublicGeneralSettings } from '@agos/api-client/model'

/**
 * `/advanced` — a settings page that never loaded, never saved, and invented two device statuses.
 *
 * **FB-150.** The whole page is a literal:
 *
 * ```tsx
 * const [settings, setSettings] = useState({
 *   autoAccept: false, smsNotif: true, printOnNew: true, closedMonday: false,
 *   maintenanceMode: false, showOnlineOrders: true, autoClose: false,
 * });
 * ```
 *
 * There is **no `fetch` anywhere in the file**, so nothing is read. The *Uložiť* button has **no `onClick`**,
 * so nothing is written. Seven switches that move and mean nothing.
 *
 * And two cards below them report hardware:
 *
 * ```tsx
 * <p>Epson TM-T88V · Pripojená</p>  …  <div className="w-2 h-2 rounded-full bg-emerald-500" />
 * <p>PizzaMajstro-WiFi · Online</p> …  <div className="w-2 h-2 rounded-full bg-emerald-500" />
 * ```
 *
 * A printer model, a network name and two green dots, all four hard-coded. There is no printer table in the
 * schema and no endpoint that reports either — the green dot is green because it is written green.
 *
 * **Three of the seven duplicate real settings**, and they agree with reality by coincidence:
 *
 * | `/advanced` says | Real column | Value on the restored data |
 * |---|---|---|
 * | `autoAccept: false` | `restaurant_settings.auto_accept` | **false** |
 * | `maintenanceMode: false` | `restaurant_settings.maintenance_mode` | **false** |
 * | `showOnlineOrders: true` | `restaurant_settings.online_orders_enabled` | **true** |
 *
 * All three match today. None of them is read: change `auto_accept` on `/settings-general` and this page still
 * says false. The other four — `smsNotif`, `printOnNew`, `closedMonday`, `autoClose` — correspond to no column,
 * no setting and no code in either backend.
 */

/** The three that exist somewhere, and where. */
export interface MirroredFlag {
  /**
   * A field of `/settings/general`, not any string.
   *
   * This page's whole claim is that these three mirror a value owned elsewhere, so the key has to
   * be one the payload actually carries. `PublicGeneralSettings` describes that payload now
   * (BLOCKED #4), and typing the key against it makes the compiler check the claim — a flag
   * renamed on the backend stops compiling here rather than quietly rendering nothing.
   */
  key: keyof PublicGeneralSettings
  /** The page that actually owns the write. */
  ownedByPath: string
}

export const MIRRORED: readonly MirroredFlag[] = [
  { key: 'autoAccept', ownedByPath: '/settings-general' },
  { key: 'onlineOrdersEnabled', ownedByPath: '/settings-general' },
  { key: 'maintenanceMode', ownedByPath: '/settings-general' },
]

/**
 * Labels resolved per call rather than at module load.
 *
 * `tt()` in a module-level literal freezes whichever language happened to be ready — FB-137's defect two
 * directories over, and the reason `sms-settings.ts` does the same thing this way.
 */
export function mirroredLabel(flag: MirroredFlag): string {
  if (flag.key === 'autoAccept') return tt('Automatické prijatie objednávok')
  if (flag.key === 'onlineOrdersEnabled') return tt('Online objednávky')
  return tt('Servisný režim')
}

/** All three live on the same page, so the argument is the contract rather than a lookup key. */
export function ownerName(_flag: MirroredFlag): string {
  return tt('Všeobecné nastavenia')
}

/** The four that exist nowhere. */
export const INVENTED: readonly string[] = ['smsNotif', 'printOnNew', 'closedMonday', 'autoClose']

/** What each invented switch claimed, and what actually does that job — where anything does. */
export function inventedNote(key: string): string {
  if (key === 'smsNotif') {
    return tt('SMS pri zmene stavu objednávky nastavuje obrazovka Nastavenia SMS, nie táto.')
  }
  if (key === 'printOnNew') {
    return tt('Automatickú tlač lístka neriadi žiadne nastavenie - tlačí sa z pokladne.')
  }
  if (key === 'closedMonday') {
    return tt('Zatvorené dni sa nastavujú v Otváracích hodinách, po jednotlivých dňoch.')
  }
  return tt('Koniec príjmu objednávok je koniec príjmu v Otváracích hodinách, pre každý deň zvlášť.')
}

export function inventedLabel(key: string): string {
  if (key === 'smsNotif') return tt('SMS notifikácie zákazníkom')
  if (key === 'printOnNew') return tt('Tlač pri novej objednávke')
  if (key === 'closedMonday') return tt('Zatvorené v pondelok')
  return tt('Automatické zatvorenie o 22:00')
}

/** The two device cards, and the fact that nothing reports them. */
export const INVENTED_DEVICES: readonly string[] = ['printer', 'network']

export function deviceNote(): string {
  return tt(
    'Stav tlačiarne a siete tu bol napísaný ako text - zelený bod bol zelený vždy. Nič taký stav nehlási, takže sa nezobrazuje.',
  )
}

export function pageNote(): string {
  return tt(
    'Táto obrazovka nikdy nič nenačítala ani neuložila: sedem prepínačov malo pevne dané hodnoty a tlačidlo Uložiť nemalo obsluhu. Tri z nich patria Všeobecným nastaveniam, štyri neexistujú nikde.',
  )
}

/**
 * Whether the page has anything of its own to offer.
 *
 * `false` — and saying so is the whole point. Kept as a named value so the page reads as a statement rather
 * than an accident of having nothing to render.
 */
export const HAS_OWN_SETTINGS = false
